import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import type {
  MotionPreference,
  PresentationPreferences,
  QualityTier,
} from '@aztec-realms/shared-types';

const STORAGE_KEY = 'aztec-realms:presentation-preferences';

const defaults: PresentationPreferences = {
  qualityTier: 'auto',
  motionPreference: 'system',
  interfaceScale: 'default',
  highContrast: false,
};

interface PresentationSettingsValue extends PresentationPreferences {
  reducedMotion: boolean;
  setQualityTier: (tier: QualityTier) => void;
  setMotionPreference: (preference: MotionPreference) => void;
  setInterfaceScale: (scale: PresentationPreferences['interfaceScale']) => void;
  setHighContrast: (enabled: boolean) => void;
}

const PresentationSettingsContext = createContext<PresentationSettingsValue | null>(null);

function readStoredPreferences(): PresentationPreferences {
  if (typeof window === 'undefined') return defaults;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaults;
    const value = JSON.parse(stored) as Partial<PresentationPreferences>;
    return {
      qualityTier: ['auto', 'low', 'medium', 'high'].includes(value.qualityTier ?? '')
        ? (value.qualityTier as QualityTier)
        : defaults.qualityTier,
      motionPreference: ['system', 'reduce', 'full'].includes(value.motionPreference ?? '')
        ? (value.motionPreference as MotionPreference)
        : defaults.motionPreference,
      interfaceScale: value.interfaceScale === 'large' ? 'large' : 'default',
      highContrast: value.highContrast === true,
    };
  } catch {
    return defaults;
  }
}

function systemPrefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function PresentationSettingsProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState(readStoredPreferences);
  const [systemReducedMotion, setSystemReducedMotion] = useState(systemPrefersReducedMotion);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setSystemReducedMotion(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const reducedMotion =
    preferences.motionPreference === 'reduce' ||
    (preferences.motionPreference === 'system' && systemReducedMotion);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    document.documentElement.dataset.motion = reducedMotion ? 'reduce' : 'full';
    document.documentElement.dataset.quality = preferences.qualityTier;
    document.documentElement.dataset.uiScale = preferences.interfaceScale;
    document.documentElement.dataset.contrast = preferences.highContrast ? 'high' : 'standard';
  }, [preferences, reducedMotion]);

  const value = useMemo<PresentationSettingsValue>(
    () => ({
      ...preferences,
      reducedMotion,
      setQualityTier: (qualityTier) => setPreferences((current) => ({ ...current, qualityTier })),
      setMotionPreference: (motionPreference) =>
        setPreferences((current) => ({ ...current, motionPreference })),
      setInterfaceScale: (interfaceScale) =>
        setPreferences((current) => ({ ...current, interfaceScale })),
      setHighContrast: (highContrast) =>
        setPreferences((current) => ({ ...current, highContrast })),
    }),
    [preferences, reducedMotion],
  );

  return (
    <PresentationSettingsContext.Provider value={value}>
      {children}
    </PresentationSettingsContext.Provider>
  );
}

export function usePresentationSettings(): PresentationSettingsValue {
  const value = useContext(PresentationSettingsContext);
  if (!value)
    throw new Error('usePresentationSettings must be used inside PresentationSettingsProvider');
  return value;
}
